import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Archive, CreditCard, KeyRound, ShieldCheck, UserCircle, UserCog, Users } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../contexts/AuthContext";
import { useTeam } from "../contexts/TeamContext";
import { api } from "../lib/chalkApi";
import {
  ChalkCard, DarkHeader, SectionTitle, UpgradeGateCard, WhiteHeader,
} from "../components/chalk/primitives";
import WrapSeasonSheet from "../components/chalk/WrapSeasonSheet";
import NotificationsCard from "../components/chalk/NotificationsCard";
import AlertPrefsCard from "../components/chalk/AlertPrefsCard";
import JoinByCodeSheet from "../components/chalk/JoinByCodeSheet";
import AddToHomeScreenCard from "../components/chalk/AddToHomeScreenCard";
import ProfileSheet from "../components/chalk/ProfileSheet";
import TimezoneSettingsCard from "../components/chalk/TimezoneSettingsCard";
import { EmailTipsCard, RedeemCodeCard } from "../components/chalk/SettingsExtras";
import { startCheckout } from "../lib/checkout";
import { UPGRADE_GATES } from "../lib/upgradeGates";

const ROLE_LABELS = {
  coach: "Coach",
  guardian: "Parent",
  league_admin: "League Admin",
  assistant: "Team Mom / Staff",
};

export default function More() {
  const { user, signout, switchRole } = useAuth();
  const { activeTeam, teams, tier, refresh, refreshTier } = useTeam();
  const navigate = useNavigate();
  const location = useLocation();
  const Header = user.role === "guardian" ? WhiteHeader : DarkHeader;
  const [roleOptions, setRoleOptions] = useState({ available: [user.role], can_switch: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.roleOptions();
        if (!cancelled) setRoleOptions(d);
      } catch { /* keep default */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const [wrapOpen, setWrapOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [billing, setBilling] = useState(null); // {tier, status, expired}
  const [switching, setSwitching] = useState(false);

  const canWrap = user.role !== "guardian" && activeTeam && activeTeam.status !== "archived" && activeTeam.is_owner;

  const reloadBilling = async () => {
    try {
      const d = await api.mySubscription();
      setBilling({
        subscription: d.subscription || null,
        expired: d.expired || null,
      });
    } catch {
      setBilling(null);
    }
  };

  useEffect(() => {
    reloadBilling();
    // If we just returned from the Stripe portal, refresh everything that
    // depends on subscription state. Stripe webhook is the source of truth;
    // this is just the client-side sync after redirect.
    const params = new URLSearchParams(location.search);
    if (params.get("portal_return") === "1" || params.get("demo_portal") === "1") {
      refreshTier();
      // Strip the query so this doesn't fire on every render
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const sub = billing?.subscription;
  const isPaid = sub && (sub.status === "active" || sub.status === "trialing");
  const isPaidTier = isPaid && (sub.tier === "chalkboard" || sub.tier === "clipboard_plus");
  // Portal also makes sense for recently-expired paid users (resume/update card)
  const canOpenPortal = isPaidTier
    || (billing?.expired && ["chalkboard", "clipboard_plus"].includes(billing.expired.tier));

  async function onManageSubscription() {
    setOpeningPortal(true);
    try {
      const d = await api.openPortal();
      if (!d?.portal_url) throw new Error("Couldn't open billing portal.");
      window.location.href = d.portal_url;
    } catch (e) {
      toast.error(e.message || "Couldn't open billing portal.");
      setOpeningPortal(false);
    }
  }

  return (
    <>
      <Header eyebrow="Settings" title="More" subtitle={`Signed in as ${user.display_name || user.first_name}`} />
      <div style={{ padding: "0 18px 24px" }}>
        <SectionTitle label="Account" />
        <ChalkCard
          data-testid="profile-card"
          onClick={() => setProfileOpen(true)}
          style={{ cursor: "pointer" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 56, height: 56, borderRadius: "50%",
              background: user.avatar_url ? "none"
                : `linear-gradient(135deg, ${user.avatar_color || "#2E7D52"}, #1F5C3C)`,
              border: "0.5px solid var(--chalk-border)",
              overflow: "hidden",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontFamily: "var(--font-display, 'Bebas Neue', sans-serif)",
              fontSize: 28, letterSpacing: "0.04em", flexShrink: 0,
            }}>
              {user.avatar_url
                ? <img src={user.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : ((user.display_name || user.first_name || user.email)[0] || "?").toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--chalk-board)" }}>
                {user.display_name || `${user.first_name} ${user.last_name}`.trim() || user.email}
              </div>
              <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {user.email}
              </div>
              <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 4, textTransform: "capitalize" }}>
                {user.role.replace("_", " ")}
                {activeTeam && user.role !== "guardian" ? ` · ${tierLabel(tier.coach_tier)}` : ""}
              </div>
            </div>
            <UserCircle size={18} aria-hidden="true" style={{ color: "var(--chalk-muted)", flexShrink: 0 }} />
          </div>
        </ChalkCard>

        {/* Join a team with a code — works for everyone. Parents and adult
            players use this to attach themselves to a coach's team without an
            email invite. Coaches can use it too if they want to be a guardian
            on another coach's team. Coach approves from their Roster page. */}
        <ChalkCard
          data-testid="join-by-code-card"
          onClick={() => setJoinOpen(true)}
          style={{ cursor: "pointer", marginTop: 12 }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "rgba(46,125,82,0.10)",
              color: "var(--green-mid)",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <KeyRound size={18} aria-hidden="true" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, color: "var(--chalk-board)", fontWeight: 500 }}>
                Join a team with a code
              </div>
              <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4, lineHeight: 1.4 }}>
                Got a code from a coach? Send a join request — they'll approve
                you onto the team.
              </div>
            </div>
          </div>
        </ChalkCard>

        {/* Role switcher — Chalk OS supports a single account being both a
            parent AND a coach. Tapping this flips the account's primary role
            and rotates the JWT. Team memberships are preserved. For regular
            users this is a single coach↔guardian toggle. Master test
            accounts (is_master_test=true, used by the product owner) get a
            3-way picker that also lets them jump into League Admin. */}
        {/* Role switcher — only rendered when the user actually has >1
            eligible role (auto-detected server-side from real memberships).
            A solo coach who never received a guardian invite never sees this. */}
        {roleOptions.can_switch && (
          <MasterRoleSwitcher
            currentRole={user.role}
            switching={switching}
            roles={roleOptions.available}
            onSwitch={async (target) => {
              if (switching || target === user.role) return;
              setSwitching(true);
              try {
                await switchRole(target);
                await refresh();
                toast.success(`Switched to ${ROLE_LABELS[target] || target} view.`);
                navigate("/app", { replace: true });
              } catch (e) {
                toast.error(e.message || "Couldn't switch role.");
              } finally {
                setSwitching(false);
              }
            }}
          />
        )}
        {/* Legacy block removed — see git history for the prior coach↔guardian
            toggle. The new auto-detected switcher above covers both regular
            and master-test users with a single component. */}
        {false && (user.role === "coach" || user.role === "guardian" || user.role === "league_admin") && (
          user.is_master_test ? (
            <MasterRoleSwitcher
              currentRole={user.role}
              switching={switching}
              onSwitch={async (target) => {
                if (switching || target === user.role) return;
                setSwitching(true);
                try {
                  await switchRole(target);
                  await refresh();
                  toast.success(`Switched to ${ROLE_LABELS[target]} view.`);
                  navigate("/app", { replace: true });
                } catch (e) {
                  toast.error(e.message || "Couldn't switch role.");
                } finally {
                  setSwitching(false);
                }
              }}
            />
          ) : (
          <ChalkCard
            data-testid="role-switcher-card"
            onClick={async () => {
              if (switching) return;
              const target = user.role === "coach" ? "guardian" : "coach";
              setSwitching(true);
              try {
                await switchRole(target);
                await refresh();
                toast.success(
                  target === "coach"
                    ? "Switched to Coach view."
                    : "Switched to Parent view.",
                );
                navigate("/app", { replace: true });
              } catch (e) {
                toast.error(e.message || "Couldn't switch role.");
              } finally {
                setSwitching(false);
              }
            }}
            style={{
              cursor: switching ? "wait" : "pointer",
              opacity: switching ? 0.7 : 1,
              marginTop: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: "rgba(46,125,82,0.10)",
                color: "var(--green-mid)",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <UserCog size={18} aria-hidden="true" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, color: "var(--chalk-board)", fontWeight: 500 }}>
                  {user.role === "coach" ? "Switch to Parent view" : "Switch to Coach view"}
                </div>
                <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4, lineHeight: 1.4 }}>
                  {user.role === "coach"
                    ? "See the Clipboard like a parent — RSVPs, schedules, what to bring."
                    : "Open the Chalkboard — run practices, post Chalk Talk, manage your team."}
                </div>
              </div>
            </div>
          </ChalkCard>
          )
        )}

        {/* Install as PWA — handles Android/Chrome `beforeinstallprompt`
            natively + falls back to a 3-step iOS Safari instructions sheet. */}
        <AddToHomeScreenCard />

        {user.role !== "guardian" && tier.coach_tier !== "chalkboard" && tier.coach_tier !== "league_os" && (
          <>
            <SectionTitle label="Upgrade" />
            <UpgradeGateCard
              {...UPGRADE_GATES.erase_and_redraw}
              context={{ source_screen: "more", current_tier: tier.coach_tier, team_id: activeTeam?.id }}
              onUpgrade={async () => {
                try {
                  await startCheckout({
                    tier: "chalkboard",
                    teamId: activeTeam?.id,
                    context: { feature_name: UPGRADE_GATES.erase_and_redraw.featureName, source_screen: "more", current_tier: tier.coach_tier },
                  });
                } catch (e) {
                  if (e?.code === "STRIPE_NOT_CONFIGURED" || e?.code === "STRIPE_PRICE_MISSING") {
                    toast.info("Upgrade isn't available yet — email support@getchalkos.com.");
                  } else {
                    toast.error(e.message);
                  }
                }
              }}
            />
          </>
        )}

        {user.role === "guardian" && tier.guardian_tier !== "clipboard_plus" && (
          <>
            <SectionTitle label="Upgrade" />
            <UpgradeGateCard
              featureName="Clipboard+"
              target="Clipboard+"
              price="$7.99/month"
              benefit="Every kid's schedule in one Family Sports Dashboard, same-day conflict flags, and full message history."
              onUpgrade={async () => {
                try { await startCheckout({ tier: "clipboard_plus" }); }
                catch (e) {
                  if (e?.code === "STRIPE_NOT_CONFIGURED" || e?.code === "STRIPE_PRICE_MISSING") {
                    toast.info("Upgrade isn't available yet — email support@getchalkos.com.");
                  } else {
                    toast.error(e.message);
                  }
                }
              }}
            />
          </>
        )}

        {canOpenPortal && (
          <>
            <SectionTitle label="Billing" />
            <ChalkCard onClick={onManageSubscription} data-testid="manage-subscription-card" style={{ cursor: openingPortal ? "wait" : "pointer", opacity: openingPortal ? 0.7 : 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ background: "#FBF8F2", borderRadius: 10, padding: 8, color: "var(--chalk-board)" }}>
                  <CreditCard size={16} aria-hidden="true" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, color: "var(--chalk-board)", fontWeight: 500 }}>
                    {openingPortal ? "Opening Stripe…" : "Manage subscription"}
                  </div>
                  <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4 }}>
                    Update card · view invoices · cancel or resume — all through Stripe.
                  </div>
                </div>
              </div>
            </ChalkCard>
          </>
        )}

        {canWrap && (
          <>
            <SectionTitle label="Season" />
            <ChalkCard onClick={() => setWrapOpen(true)} data-testid="wrap-season-card" style={{ cursor: "pointer" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{ background: "#FBF8F2", borderRadius: 10, padding: 8, color: "var(--chalk-board)" }}>
                  <Archive size={16} aria-hidden="true" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, color: "var(--chalk-board)", fontWeight: 500 }}>Wrap this season</div>
                  <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4 }}>
                    Archive {activeTeam.season_label} and roll the roster forward.
                  </div>
                </div>
              </div>
            </ChalkCard>
          </>
        )}

        {teams.some((t) => t.status === "archived") && (
          <>
            <SectionTitle label="Past seasons" />
            {teams.filter((t) => t.status === "archived").map((t) => (
              <ChalkCard key={t.id} data-testid={`archived-${t.id}`} style={{ opacity: 0.85 }}>
                <div style={{ fontSize: 14, color: "var(--chalk-board)", fontWeight: 500 }}>
                  {t.name} · {t.season_label}
                </div>
                <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 4 }}>
                  Read-only · {t.athlete_count} athlete{t.athlete_count === 1 ? "" : "s"}
                </div>
              </ChalkCard>
            ))}
          </>
        )}

        {(user.email.endsWith("@chalkdemo.com") || user.role === "league_admin") && (
          <>
            <SectionTitle label="Tools" />
            <AdminLink to="/app/files" label="Chalk Lines (files)" />
            <AdminLink to="/app/volunteers" label="Volunteers" />
            {user.role === "league_admin" && (
              <AdminLink to="/app/league" label="League Admin" />
            )}
            {user.is_admin && <>
            <SectionTitle label="Admin" />
            <AdminLink to="/admin/overview" label="Operational dashboard" />
            <AdminLink to="/admin/billing" label="Billing controls" />
            <AdminLink to="/admin/notifications" label="Notification log (Twilio stub)" />
            <AdminLink to="/admin/emails" label="Email preview (SendGrid stub)" />
            <AdminLink to="/admin/jobs" label="Background jobs" />
            <AdminLink to="/admin/audit" label="Audit log" />
            <AdminLink to="/admin/permission-tests" label="Permission tests" />
            <AdminLink to="/admin/voluntold" label="Voluntold funnel + kit codes" />
            </>}
          </>
        )}
        {!(user.email.endsWith("@chalkdemo.com") || user.role === "league_admin") && (
          <>
            <SectionTitle label="Tools" />
            <AdminLink to="/app/files" label="Chalk Lines (files)" />
            <AdminLink to="/app/volunteers" label="Volunteers" />
          </>
        )}

        <SectionTitle label="Tournaments" />
        <AdminLink to="/app/tournaments" label="Run a tournament"
                   testId="more-run-tournament" />

        <SectionTitle label="Notifications" />
        <NotificationsCard />
        <AlertPrefsCard />

        <SectionTitle label="Settings" />
        <TimezoneSettingsCard />
        <EmailTipsCard />
        {user.role !== "guardian" && <RedeemCodeCard />}

        <SectionTitle label="Session" />
        <ChalkCard onClick={() => { signout(); navigate("/"); }} data-testid="more-signout">
          <div style={{ fontSize: 15, color: "var(--chalk-red)", fontWeight: 500 }}>Sign out</div>
        </ChalkCard>
      </div>

      <WrapSeasonSheet
        isOpen={wrapOpen}
        team={activeTeam}
        onDismiss={() => setWrapOpen(false)}
        onWrapped={() => { setWrapOpen(false); refresh(); }}
      />
      <JoinByCodeSheet
        isOpen={joinOpen}
        onDismiss={() => setJoinOpen(false)}
        onJoined={() => { setJoinOpen(false); refresh(); }}
      />
      <ProfileSheet
        isOpen={profileOpen}
        user={user}
        onDismiss={() => setProfileOpen(false)}
        onSaved={() => { setProfileOpen(false); refresh(); }}
      />
    </>
  );
}

function AdminLink({ to, label }) {
  return (
    <Link to={to} style={{ textDecoration: "none" }}>
      <ChalkCard style={{ marginTop: 8 }}>
        <div style={{ fontSize: 15, color: "var(--chalk-board)" }}>{label} →</div>
      </ChalkCard>
    </Link>
  );
}

function tierLabel(t) {
  return {
    pickup: "Pickup (free)",
    clipboard_plus: "The Clipboard+",
    chalkboard: "The Chalkboard",
    league_os: "League OS",
  }[t] || t;
}



function MasterRoleSwitcher({ currentRole, switching, onSwitch, roles }) {
  const ALL_OPTIONS = [
    { value: "coach",        label: "Coach",        icon: <UserCog size={16} aria-hidden="true" />,    caption: "Run team — Chalkboard, Playbook, Chalk Talk." },
    { value: "guardian",     label: "Parent / Player", icon: <Users size={16} aria-hidden="true" />,   caption: "Clipboard view — RSVPs, what to bring, schedule." },
    { value: "assistant",    label: "Team Mom / Staff", icon: <Users size={16} aria-hidden="true" />,  caption: "Help run a team — volunteers, snacks, announcements." },
    { value: "league_admin", label: "League Admin", icon: <ShieldCheck size={16} aria-hidden="true" />, caption: "League OS — manage teams, broadcasts, league insights." },
  ];
  // Restrict to roles the user is actually eligible for (auto-detected by the
  // backend `_available_roles_for`). Falls back to showing all on legacy
  // callers that don't pass a roles prop.
  const options = roles && roles.length
    ? ALL_OPTIONS.filter((o) => roles.includes(o.value))
    : ALL_OPTIONS;
  return (
    <ChalkCard
      data-testid="master-role-switcher"
      style={{ marginTop: 12, opacity: switching ? 0.7 : 1 }}
    >
      <div style={{
        fontSize: 11, textTransform: "uppercase", letterSpacing: "0.10em",
        color: "var(--chalk-muted)", fontWeight: 700, marginBottom: 4,
      }}>
        Test account · switch role
      </div>
      <div style={{ fontSize: 12, color: "var(--chalk-muted)", lineHeight: 1.5, marginBottom: 10 }}>
        Master test account — hop between every dashboard to exercise the
        full surface. Subscription state is preserved.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {options.map((opt) => {
          const active = opt.value === currentRole;
          return (
            <button
              key={opt.value}
              type="button"
              disabled={switching || active}
              onClick={() => onSwitch(opt.value)}
              data-testid={`master-role-${opt.value}`}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "11px 12px", borderRadius: 10,
                background: active ? "var(--chalk-board)" : "#fff",
                color: active ? "#fff" : "var(--chalk-board)",
                border: active ? "1px solid var(--chalk-board)"
                               : "0.5px solid var(--chalk-border)",
                fontFamily: "var(--font-body)", textAlign: "left",
                cursor: switching ? "wait" : active ? "default" : "pointer",
              }}
            >
              <div style={{
                width: 30, height: 30, borderRadius: 8,
                background: active ? "rgba(255,255,255,0.12)" : "rgba(46,125,82,0.08)",
                color: active ? "#fff" : "var(--green-mid)",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                {opt.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>
                  {opt.label}{active ? " · current" : ""}
                </div>
                <div style={{ fontSize: 11, opacity: 0.82, marginTop: 2, lineHeight: 1.4 }}>
                  {opt.caption}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </ChalkCard>
  );
}
